import styled from '@emotion/styled';
import { Button, type SelectOption } from '@innovationdepartment/proxima-ui';
import { useEffect, useState } from 'react';
import {
  BackLink,
  CheckboxLabel,
  CheckboxRow,
  ContentSection,
  FormField,
  FormRow,
  Header,
  HeaderLeft,
  Label,
  LoadingText,
  PageWrapper,
  SectionTitle,
  StyledCheckbox,
  StyledInput,
  StyledSelect,
  TextArea,
  Title
} from './BrandTags.styles';

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
`;

export interface CreateTagViewProps {
  formData: {
    displayName: string;
    description: string;
    category: string;
    subCategory: string;
    genderRelevance: string;
    typeDisplayName: string;
    typeHelperText: string;
    brandAssignmentGuidance: string;
    personaRelevanceGuidance: string;
    personaEligible: boolean;
    sensitive: boolean;
    deprecated: boolean;
  };
  errors: Set<string>;
  onFieldChange: (field: string, value: string | boolean) => void;
  onBack: () => void;
  onCreate: () => void;
  categoryOptions: Array<SelectOption>;
  subCategoryOptions: Array<SelectOption>;
  genderOptions: Array<SelectOption>;
  onCategoryChange: (value: string) => void;
  onSubCategoryChange: (value: string) => void;
  dataLoaded: boolean;
}

const CreateBrandTagView = ({
  formData,
  errors,
  onFieldChange,
  onBack,
  onCreate,
  categoryOptions,
  subCategoryOptions,
  genderOptions,
  onCategoryChange,
  onSubCategoryChange,
  dataLoaded
}: CreateTagViewProps) => {
  // Use keys to force re-render of Select components when values change
  const [categoryKey, setCategoryKey] = useState(0);
  const [subCategoryKey, setSubCategoryKey] = useState(0);

  // Force re-render when category value changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    setCategoryKey(prev => prev + 1);
  }, [formData.category]);

  // Force re-render when subcategory value changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    setSubCategoryKey(prev => prev + 1);
  }, [formData.subCategory]);

  if (!dataLoaded) {
    return (
      <PageWrapper>
        <LoadingText>Loading...</LoadingText>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header>
        <HeaderLeft>
          <BackLink onClick={onBack}>[← Back to Tags]</BackLink>
          <Title>CREATE NEW TAG</Title>
        </HeaderLeft>
        <HeaderRight>
          <Button label="Create" size="small" color="success" onClick={onCreate} />
        </HeaderRight>
      </Header>

      <ContentSection>
        <SectionTitle>TAG PROPERTIES</SectionTitle>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('displayName')}>Name:</Label>
            <StyledInput
              type="text"
              value={formData.displayName}
              onChange={e => onFieldChange('displayName', e.target.value)}
              placeholder="Enter name"
              hasError={errors.has('displayName')}
              data-testid="display-name-input"
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('description')}>Description:</Label>
            <TextArea
              value={formData.description}
              onChange={e => onFieldChange('description', e.target.value)}
              placeholder="Enter description"
              hasError={errors.has('description')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('category')}>Category:</Label>
            <StyledSelect
              key={categoryKey}
              value={formData.category}
              onChange={e => onCategoryChange(e.target.value)}
              hasError={errors.has('category')}
            >
              {categoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </FormField>
          <FormField>
            <Label hasError={errors.has('subCategory')}>Sub-Category:</Label>
            <StyledSelect
              key={subCategoryKey}
              value={formData.subCategory}
              onChange={e => onSubCategoryChange(e.target.value)}
              hasError={errors.has('subCategory')}
            >
              <option value="">Select sub-category</option>
              {subCategoryOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField flex={2}>
            <Label hasError={errors.has('genderRelevance')}>Gender Relevance:</Label>
            <StyledSelect
              value={formData.genderRelevance}
              onChange={e => onFieldChange('genderRelevance', e.target.value)}
              hasError={errors.has('genderRelevance')}
            >
              {genderOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </StyledSelect>
          </FormField>
          <FormField flex={2} />
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('typeDisplayName')}>Type Display:</Label>
            <StyledInput
              type="text"
              value={formData.typeDisplayName}
              onChange={e => onFieldChange('typeDisplayName', e.target.value)}
              placeholder="Enter Type Display"
              hasError={errors.has('typeDisplayName')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('typeHelperText')}>Type Helper Text:</Label>
            <TextArea
              value={formData.typeHelperText}
              onChange={e => onFieldChange('typeHelperText', e.target.value)}
              placeholder="Enter Type Helper Text"
              hasError={errors.has('typeHelperText')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('brandAssignmentGuidance')}>
              Brand Assignment Guidance:
            </Label>
            <StyledInput
              type="text"
              value={formData.brandAssignmentGuidance}
              onChange={e => onFieldChange('brandAssignmentGuidance', e.target.value)}
              placeholder="Enter Brand Assignment Guidance"
              hasError={errors.has('brandAssignmentGuidance')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <Label hasError={errors.has('personaRelevanceGuidance')}>
              Persona Relevance Guidance:
            </Label>
            <StyledInput
              type="text"
              value={formData.personaRelevanceGuidance}
              onChange={e => onFieldChange('personaRelevanceGuidance', e.target.value)}
              placeholder="Enter Persona Relevance Guidance"
              hasError={errors.has('personaRelevanceGuidance')}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <CheckboxRow>
            <StyledCheckbox
              type="checkbox"
              checked={formData.personaEligible}
              onChange={e => onFieldChange('personaEligible', e.target.checked)}
            />
            <CheckboxLabel>Persona Eligible</CheckboxLabel>
          </CheckboxRow>
          <CheckboxRow>
            <StyledCheckbox
              type="checkbox"
              checked={formData.sensitive}
              onChange={e => onFieldChange('sensitive', e.target.checked)}
            />
            <CheckboxLabel>Sensitive</CheckboxLabel>
          </CheckboxRow>
          <CheckboxRow>
            <StyledCheckbox
              type="checkbox"
              checked={formData.deprecated}
              onChange={e => onFieldChange('deprecated', e.target.checked)}
            />
            <CheckboxLabel>Deprecated</CheckboxLabel>
          </CheckboxRow>
        </FormRow>
      </ContentSection>
    </PageWrapper>
  );
};

export default CreateBrandTagView;
